import { useSelector } from 'react-redux';
import { useGetMerchantQuery, useLazyGetMerchantsListQuery } from 'services/merchants';
import { MerchantStatus, MerchantsListQuery } from 'services/merchants/types';
import pwcOrdersApi from 'services/pwcOrders';
import { useUploadAssetMutation } from 'services/staticAssets';

import { SelectItem } from 'components/inputs/select/select';

import { LoadingScreen } from 'pages/loading/loading';

import selectAccount from 'state/selectors/accounts/accountSelector';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountType } from 'types/account';
import { APIError } from 'types/error';
import { RequestPagination, ServerPagination } from 'types/pagination';
import { CreateOrderParams } from 'types/pwcOrder';
import { UserAccessLevel } from 'types/user';

import PaymentByLinkCreate from '.';
import PageUnavailable from './pageUnavailable';

const PaymentByLinkContainer = () => {
  const { account } = useSelector(selectAccount);
  const [createOrderMutation, { isLoading, error }] = pwcOrdersApi.useCreateOrderMutation();
  const [uploadAsset, { isLoading: isAssetUploading, reset: resetUpload }] = useUploadAssetMutation();

  const userLevel = useSelector(selectUserLevel);
  const isSelectAccountPSPOrGA = [AccountType.PSP, AccountType.Pwc_Group].includes(account.type);
  const canCreateOrdersForDifferentMerchants = isSelectAccountPSPOrGA || Boolean(userLevel && userLevel > UserAccessLevel.Support);
  const accountsMerchantUuid = canCreateOrdersForDifferentMerchants ? undefined : account.uuid;
  const { isLoading: isMerchantInfoLoading, data: merchantInfo } = useGetMerchantQuery(account.uuid, {
    skip: !account.uuid || canCreateOrdersForDifferentMerchants,
  });
  const isInactiveMerchant = !merchantInfo || merchantInfo.data.status !== MerchantStatus.Active;
  const isPageUnavailable = !canCreateOrdersForDifferentMerchants && isInactiveMerchant;

  const [fetchMerchants] = useLazyGetMerchantsListQuery();

  const createOrder = (payload: Omit<CreateOrderParams, 'merchantUuid'>, merchantUuid?: string) => {
    if (!merchantUuid && !accountsMerchantUuid) {
      throw new Error('Merchant UUID is required');
    }
    return createOrderMutation({ ...payload, merchantUuid: merchantUuid || accountsMerchantUuid! });
  };

  const getActiveMerchants = async (params: {
    search?: string;
    pagination?: RequestPagination;
  }): Promise<{ data: SelectItem[]; pagination?: ServerPagination }> => {
    const query: MerchantsListQuery = {
      pagination: params.pagination,
      filters: {
        status: MerchantStatus.Active,
        pwcEnabled: true,
        ...(userLevel === UserAccessLevel.Partner && account.uuid ? { accountUuids: [account.uuid] } : {}),
      },
    };

    if (params.search) {
      query.search = params.search;
    }

    const response = await fetchMerchants(query);

    if ('data' in response) {
      const records: SelectItem[] = response.data!.data.map((item) => ({
        key: item.uuid,
        label: item.name,
        value: item,
      }));

      return {
        data: records,
        pagination: response.data!.pagination,
      };
    }

    return { data: [] };
  };

  if (isMerchantInfoLoading) {
    return <LoadingScreen />;
  }
  if (isPageUnavailable) {
    return <PageUnavailable />;
  }

  return (
    <PaymentByLinkCreate
      merchant={merchantInfo?.data}
      getMerchants={getActiveMerchants}
      canCreateOrdersForDifferentMerchants={canCreateOrdersForDifferentMerchants}
      readOnlyMode={false}
      orderCreation={{
        isLoading,
        error,
        createOrder: createOrder as (payload: Omit<CreateOrderParams, 'merchantUuid'>) => Promise<any>,
      }}
      assetUpload={{
        uploadAsset: uploadAsset as (file: File) => Promise<{ data: string; error?: { data: APIError } }>,
        isAssetUploading,
        resetUpload,
      }}
    />
  );
};

export default PaymentByLinkContainer;
